// Auth token storage with native-secure fallback.
//
// Web / preview / dev (non-Capacitor): localStorage (current behaviour).
// iOS / Android (Capacitor): @capacitor/preferences, which on iOS persists
//   to the iOS Keychain through Capacitor's underlying CapacitorKeychain
//   bridge — survives app upgrades, NOT wiped by iOS storage cleanup, NOT
//   readable by any other app.
//
// Same get/set/remove surface as `window.localStorage` so the rest of the
// app doesn't care which backend is active. ALL calls are async on iOS but
// the in-memory hot cache lets `getToken()` stay synchronous after the
// first `loadTokens()` at app boot.
//
// Keys:
//   "vital_token"   — short-lived access JWT (24h)
//   "vital_refresh" — long-lived refresh JWT (90d)  — NATIVE ONLY storage
import { Capacitor } from "@capacitor/core";

const KEY_ACCESS  = "vital_token";
const KEY_REFRESH = "vital_refresh";

const isNative = () =>
    Capacitor.getPlatform && Capacitor.getPlatform() !== "web";

// Hot cache — populated by loadTokens() at boot, kept in sync by writers.
const cache = { access: null, refresh: null, loaded: false };

// Synchronous browser-side hydration AT IMPORT TIME — so the very first
// API request (often /auth/me from AuthContext or a peer component
// mounted alongside it) already carries the bearer header. On native
// (iOS Capacitor) we have to wait for `loadTokens()` which is awaited
// in AuthContext before `refresh()` fires.
try {
    if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
        const platform = (typeof Capacitor !== "undefined"
                          && Capacitor.getPlatform
                          && Capacitor.getPlatform()) || "web";
        if (platform === "web") {
            cache.access  = window.localStorage.getItem("vital_token")   || null;
            cache.refresh = window.localStorage.getItem("vital_refresh") || null;
            cache.loaded  = true;
        }
    }
} catch { /* pre-render / SSR / unavailable storage */ }

// Cached lazy-import wrapped in a plain object. CRITICAL detail: we
// must NEVER return the raw Capacitor plugin proxy from an async
// function. V8 does `Promise.resolve(returnValue)` on every async
// return, which probes for thenables via `if (typeof v.then ===
// "function")`. The Capacitor plugin proxy intercepts EVERY property
// access — so `.then` returns a function, Promise.resolve thinks the
// plugin is a thenable, calls `Preferences.then(resolve, reject)`,
// which the native bridge logs as "Preferences.then() is not
// implemented on ios". That floods Diagnostics with unhandled-
// rejection storms and (worst case) crashes the WebView so iOS
// reloads back to /login. Wrapping in a plain object kills the
// thenable detection.
let _prefsCache = null;
async function _prefs() {
    if (_prefsCache) return _prefsCache;
    const mod = await import("@capacitor/preferences");
    _prefsCache = { plugin: mod.Preferences };
    return _prefsCache;
}

/**
 * Hydrate the in-memory cache from disk. Call ONCE at app startup before
 * any API request fires. Idempotent — safe to call multiple times.
 *
 * HARD TIMEOUT (1.5s): if the Capacitor Preferences plugin isn't
 * responding (e.g. plugin not synced into the iOS bundle, native bridge
 * stuck), we bail out and the app continues with whatever cache state
 * we have. Without this, the AuthProvider's first `/auth/me` would
 * never fire and the user would stare at a loading spinner forever.
 * Symptom seen on TestFlight after the first deploy of this module.
 */
export async function loadTokens() {
    if (cache.loaded) return cache;
    const work = (async () => {
        try {
            if (isNative()) {
                const { plugin: P } = await _prefs();
                const a = await P.get({ key: KEY_ACCESS });
                const r = await P.get({ key: KEY_REFRESH });
                cache.access  = a?.value || null;
                cache.refresh = r?.value || null;
            } else {
                cache.access  = window.localStorage.getItem(KEY_ACCESS)  || null;
                cache.refresh = window.localStorage.getItem(KEY_REFRESH) || null;
            }
        } catch {
            // First-launch on a fresh device — nothing in store yet.
        }
    })();
    const timeout = new Promise((resolve) => setTimeout(resolve, 1500));
    await Promise.race([work, timeout]);
    cache.loaded = true;
    return cache;
}

/** Synchronous read after loadTokens() has resolved. */
export function getAccessToken() {
    return cache.access;
}

export function getRefreshToken() {
    return cache.refresh;
}

/**
 * Persist BOTH tokens — called after every successful login / refresh.
 * Pass `null` for either side to leave it untouched.
 *
 * Best-effort disk write with a 1.5s timeout. On TestFlight if the
 * @capacitor/preferences plugin isn't synced into the iOS bundle the
 * native call would hang forever and the user's `login()` Promise would
 * never resolve — they'd see a frozen "Inloggen…" spinner. We update
 * the in-memory cache synchronously so the rest of the app works even
 * when persistence fails.
 */
export async function setTokens({ access, refresh } = {}) {
    // 1. Sync cache update — guarantees the next API call has the bearer.
    if (access !== undefined)  cache.access  = access;
    if (refresh !== undefined) cache.refresh = refresh;
    cache.loaded = true;

    // 2. Best-effort persistence, time-boxed.
    const write = (async () => {
        if (access !== undefined) {
            if (isNative()) {
                const { plugin: P } = await _prefs();
                if (access) await P.set({ key: KEY_ACCESS, value: access });
                else        await P.remove({ key: KEY_ACCESS });
            } else if (access) {
                window.localStorage.setItem(KEY_ACCESS, access);
            } else {
                window.localStorage.removeItem(KEY_ACCESS);
            }
        }
        if (refresh !== undefined) {
            if (isNative()) {
                const { plugin: P } = await _prefs();
                if (refresh) await P.set({ key: KEY_REFRESH, value: refresh });
                else         await P.remove({ key: KEY_REFRESH });
            } else if (refresh) {
                window.localStorage.setItem(KEY_REFRESH, refresh);
            } else {
                window.localStorage.removeItem(KEY_REFRESH);
            }
        }
    })().catch(() => { /* swallow — cache is the source of truth this session */ });
    const timeout = new Promise((resolve) => setTimeout(resolve, 1500));
    await Promise.race([write, timeout]);
}

export async function clearTokens() {
    return setTokens({ access: null, refresh: null });
}
